import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Fieldset } from 'primereact/fieldset'

import {
  Badge,
  Btn,
  Cnpj,
  ConfirmDialog,
  CustomDataTableHeader,
  CustomMultiSelect,
  CustomPanel,
  DateTime,
  Loading,
  NumeroDecimal,
  PageHeader,
  Periodo,
  ThreeDots,
} from '../../../../../components'

import { tableCellCentered } from '../../../../../assets/js/styles'
import { showMessageBox } from '../../../../../store/actions/system.actions'
import { errorHandler } from '../../../../../services/util/error.service'
import { getPeriodoMesAtual } from '../../../../../services/util/date.service'
import { downloadFile } from '../../../../../services/util/download.service'
import {
  deleteCteOrigem,
  getCtesOrigem,
  getXmlCteOrigem,
  postImportarCteOrigem,
} from '../../../../../services/api/https/deliveries/cte.http'
import ModalEmitirCteSubcontratacao from './ModalEmitirCteSubcontratacao'
import {
  FILTROS_CTE_ORIGEM_INICIAIS,
  aplicarFiltrosCteOrigem,
  badgeEmissaoCteOrigem,
  erroEmissaoCteOrigem,
  erroExclusaoCteOrigem,
  formatarChaveCte,
  lerArquivosXml,
  mensagemImportacaoCteOrigem,
  opcoesFiltroCteOrigem,
  rotaCteOrigem,
} from './cteSubcontratacao.util'

const MENU_EMITIR = '1. Emitir'
const MENU_EXCLUIR = '2. Excluir'
const MENU_BAIXAR = '3. Baixar'
const MENU_ITEMS = [MENU_EMITIR, MENU_EXCLUIR, MENU_BAIXAR]

function EmissaoCteSubcontratacao({ dispatch, selectedTp, history }) {
  // Hooks
  const [ctesOrigem, setCtesOrigem] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [periodo, setPeriodo] = useState(getPeriodoMesAtual())
  const [filtros, setFiltros] = useState(FILTROS_CTE_ORIGEM_INICIAIS)
  const [globalFilter, setGlobalFilter] = useState('')
  const [confirmExcluir, setConfirmExcluir] = useState({ visible: false, cte: null })
  const [modalEmitir, setModalEmitir] = useState({ visible: false, cte: null })
  const inputXmlRef = useRef(null)

  const opcoesFiltro = useMemo(() => opcoesFiltroCteOrigem(ctesOrigem), [ctesOrigem])
  const ctesFiltrados = useMemo(
    () => aplicarFiltrosCteOrigem(ctesOrigem, filtros),
    [ctesOrigem, filtros],
  )

  useEffect(() => {
    buscarCtesOrigem(periodo.dataInicial, periodo.dataFinal)
  }, [selectedTp])

  // Events
  const onError = error => {
    dispatch(showMessageBox(null, errorHandler(error)))
  }

  const onPesquisar = (pInicial, pFinal) => {
    setPeriodo({ dataInicial: pInicial, dataFinal: pFinal })
    buscarCtesOrigem(pInicial, pFinal)
  }

  const onImportarClick = () => {
    if (!selectedTp) {
      onError(new Error('Selecione o Operador Logístico'))
      return
    }
    inputXmlRef.current.click()
  }

  const onArquivosXmlSelecionados = async evt => {
    // O evento sintético é reciclado pelo React após o handler, então o input vem do ref
    const fileList = evt.target.files
    if (!fileList || !fileList.length) return

    try {
      setIsLoading(true)
      const arquivos = await lerArquivosXml(fileList)
      const importados = await postImportarCteOrigem(selectedTp, arquivos)
      dispatch(showMessageBox(mensagemImportacaoCteOrigem(importados), null))
      await buscarCtesOrigem(periodo.dataInicial, periodo.dataFinal)
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
      if (inputXmlRef.current) inputXmlRef.current.value = ''
    }
  }

  const onMenuItemClicked = async (item, cte) => {
    try {
      switch (item) {
        case MENU_EMITIR: {
          const erroEmissao = erroEmissaoCteOrigem(cte)
          if (erroEmissao) throw new Error(erroEmissao)
          setModalEmitir({ visible: true, cte })
          break
        }
        case MENU_EXCLUIR: {
          const erroExclusao = erroExclusaoCteOrigem(cte)
          if (erroExclusao) throw new Error(erroExclusao)
          setConfirmExcluir({ visible: true, cte })
          break
        }
        case MENU_BAIXAR:
          await baixarXml(cte)
          break
        default:
          throw new Error('Item do menu incorreto')
      }
    } catch (error) {
      onError(error)
    }
  }

  const onConfirmarExclusao = async () => {
    const { cte } = confirmExcluir
    setConfirmExcluir({ visible: false, cte: null })
    try {
      setIsLoading(true)
      await deleteCteOrigem(selectedTp, cte.id)
      dispatch(showMessageBox(`CTe nº ${cte.numero} de ${cte.emitente_nome} excluído`, null))
      await buscarCtesOrigem(periodo.dataInicial, periodo.dataFinal)
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onModalEmitirSave = () => {
    dispatch(
      showMessageBox('CTe de Subcontratação enviado para emissão, aguardando resposta SEFAZ', null),
    )
    setModalEmitir({ visible: false, cte: null })
    buscarCtesOrigem(periodo.dataInicial, periodo.dataFinal)
  }

  // Helpers
  const buscarCtesOrigem = async (pInicial, pFinal) => {
    try {
      if (!selectedTp) throw new Error('Selecione o Operador Logístico')
      setIsLoading(true)
      const fetchedCtes = await getCtesOrigem(selectedTp, pInicial, pFinal)
      setCtesOrigem(fetchedCtes)
      setFiltros(FILTROS_CTE_ORIGEM_INICIAIS)
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const baixarXml = async cte => {
    const xml = await getXmlCteOrigem(selectedTp, cte.id)
    downloadFile(xml, `${cte.chave}-cte.xml`)
  }

  const setFiltro = (nome, value) => setFiltros({ ...filtros, [nome]: value || [] })

  // Render
  return (
    <>
      <PageHeader>Emissão de CTe de Subcontratação</PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel showBtnNovo={false} onVoltar={history.goBack}>
          <div className="p-grid" style={{ marginLeft: 0 }}>
            <div className="p-col-12" style={{ display: 'flex' }}>
              <Periodo
                periodoFinal={periodo.dataFinal}
                periodoInicial={periodo.dataInicial}
                onPesquisar={onPesquisar}
              />
              <div style={{ marginLeft: 10, paddingTop: 27 }}>
                <Btn onClick={onImportarClick} disabled={isLoading}>
                  Importar XML de CTe
                </Btn>
                <input
                  type="file"
                  accept=".xml,text/xml,application/xml"
                  multiple
                  ref={inputXmlRef}
                  onChange={onArquivosXmlSelecionados}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <Fieldset legend="Filtro" toggleable collapsed={true} className="p-col-12 p-mt-2">
              <div className="p-grid">
                <div className="p-col-6 p-md-3">
                  <CustomMultiSelect
                    label="Emitente"
                    value={filtros.emitentes}
                    options={opcoesFiltro.emitentes}
                    onChange={value => setFiltro('emitentes', value)}
                  />
                </div>
                <div className="p-col-6 p-md-3">
                  <CustomMultiSelect
                    label="Remetente"
                    value={filtros.remetentes}
                    options={opcoesFiltro.remetentes}
                    onChange={value => setFiltro('remetentes', value)}
                  />
                </div>
                <div className="p-col-6 p-md-3">
                  <CustomMultiSelect
                    label="Destinatário"
                    value={filtros.destinatarios}
                    options={opcoesFiltro.destinatarios}
                    onChange={value => setFiltro('destinatarios', value)}
                  />
                </div>
                <div className="p-col-6 p-md-3">
                  <CustomMultiSelect
                    label="Cidade (origem ou destino)"
                    value={filtros.cidades}
                    options={opcoesFiltro.cidades}
                    onChange={value => setFiltro('cidades', value)}
                  />
                </div>
              </div>
            </Fieldset>

            <div className="p-col-12">
              {isLoading ? (
                <div className="text-center" style={{ marginTop: 20 }}>
                  <Loading />
                </div>
              ) : (
                <DataTableComponent
                  ctesOrigem={ctesFiltrados}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  onMenuItemClicked={onMenuItemClicked}
                />
              )}
            </div>
          </div>
        </CustomPanel>
      </div>

      <ConfirmDialog
        title="Excluir CTe de origem"
        isVisible={confirmExcluir.visible}
        content={
          confirmExcluir.cte ? (
            <>
              <div>
                Tem certeza que deseja excluir o CTe nº <b>{confirmExcluir.cte.numero}</b> de{' '}
                <b>{confirmExcluir.cte.emitente_nome}</b> (chave <b>{confirmExcluir.cte.chave}</b>)?
              </div>
              <div className="p-mt-2">
                Essa ação é <b>permanente</b> e não poderá ser desfeita.
              </div>
            </>
          ) : (
            ''
          )
        }
        onOk={onConfirmarExclusao}
        onCancelar={() => setConfirmExcluir({ visible: false, cte: null })}
        okLabel="Excluir"
        cancelLabel="Cancelar"
      />

      {modalEmitir.cte && (
        <ModalEmitirCteSubcontratacao
          selectedTp={selectedTp}
          visible={modalEmitir.visible}
          cteOrigem={modalEmitir.cte}
          onModalClose={() => setModalEmitir({ visible: false, cte: null })}
          onModalSave={onModalEmitirSave}
          onError={onError}
        />
      )}
    </>
  )
}

function ParteCell({ nome, documento }) {
  return (
    <div>
      <div>{nome}</div>
      <small>
        <Cnpj>{documento}</Cnpj>
      </small>
    </div>
  )
}

function DataTableComponent({ ctesOrigem, globalFilter, setGlobalFilter, onMenuItemClicked }) {
  return (
    <DataTable
      value={ctesOrigem}
      paginator={true}
      rows={10}
      emptyMessage="Não existem CTe's de origem importados no período"
      globalFilter={globalFilter}
      header={<CustomDataTableHeader onChange={setGlobalFilter} />}
    >
      <Column
        field="chave"
        header="Chave / Nº · Série"
        body={cte => (
          <div>
            <div style={{ fontSize: '0.8rem' }}>{formatarChaveCte(cte.chave)}</div>
            <small>
              nº {cte.numero} · série {cte.serie} · mod. {cte.modelo}
            </small>
          </div>
        )}
        style={{ width: 250 }}
      />
      <Column
        field="emitente_nome"
        header="Emitente"
        body={cte => <ParteCell nome={cte.emitente_nome} documento={cte.emitente_cnpj} />}
      />
      <Column
        field="remetente_nome"
        header="Remetente"
        body={cte => <ParteCell nome={cte.remetente_nome} documento={cte.remetente_cnpj_cpf} />}
      />
      <Column
        field="destinatario_nome"
        header="Destinatário"
        body={cte => (
          <ParteCell nome={cte.destinatario_nome} documento={cte.destinatario_cnpj_cpf} />
        )}
      />
      <Column field="municipio_fim" header="Rota" body={cte => rotaCteOrigem(cte)} />
      <Column
        sortable
        field="valor_carga"
        header="Vlr. Carga"
        body={cte => <NumeroDecimal>{cte.valor_carga}</NumeroDecimal>}
        style={tableCellCentered(120)}
      />
      <Column
        sortable
        field="data_emissao"
        header="Dt. Emissão"
        body={cte => <DateTime>{cte.data_emissao}</DateTime>}
        style={tableCellCentered(110)}
      />
      <Column
        sortable
        field="created_at"
        header="Importação"
        body={cte => <DateTime>{cte.created_at}</DateTime>}
        style={tableCellCentered(110)}
      />
      <Column
        field="cte_status"
        header="Emissão"
        style={tableCellCentered(140)}
        body={cte => {
          const { color, text } = badgeEmissaoCteOrigem(cte)
          return <Badge color={color}>{text}</Badge>
        }}
      />
      <Column
        header="Menu"
        style={tableCellCentered(70)}
        body={cte => (
          <ThreeDots items={MENU_ITEMS} onClick={item => onMenuItemClicked(item, cte)} />
        )}
        bodyStyle={tableCellCentered()}
      />
    </DataTable>
  )
}

const mapToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapToProps)(withRouter(EmissaoCteSubcontratacao))
